import { UserInterface } from '../../actions/action-types';
import { AuthenticationChannelEvents } from '../../enums';

import * as authenticationContext from '../authentication-context';
import { Store } from 'redux';
import { activityMonitor } from '../session-manager/session-manager';
import { ActivityMonitor } from '../api/activity-monitor';
import { loggerFactory } from '../logger';

const authenticatedEvents = new Set([
  AuthenticationChannelEvents.BOOTSTRAP_AUTH_RESPONSE
]);

const logger = loggerFactory('AuthenticationChannel');

type ChannelDefinition = {
  channelName: string;
  child: boolean;
  browsingContext: string;
};

// logger.log statements are left commented out here bc debugging
// the channel is a pain.
export class AuthenticationChannel extends BroadcastChannel {
  readonly ready: Promise<boolean>;
  readonly isChild: boolean;
  private url: URL;
  authenticationContext: any; // just exposed here for testing purposes.
  private store: Store;
  private shortName: string;
  browsingContextId: string;

  constructor(channelDefinition: ChannelDefinition) {
    let { channelName, child, browsingContext } = channelDefinition;

    super(channelName);

    this.browsingContextId = browsingContext;
    this.shortName = `${channelName}`.slice(-10);
    this.isChild = child;
    this.authenticationContext = authenticationContext;
    this.ready = this._init();

    this.addEventListener('message', this.messageListener.bind(this));
  }

  setStore(store: Store) {
    this.store = store;
  }

  getStore() {
    return this.store;
  }

  messageListener(message: any) {
    const messageType = message.data?.type ?? '';
    logger.debug(
      `AuthenticationChannel ${this.browsingContextId} (${this.shortName}) messageListener: `,
      messageType
    );
    if (messageType === UserInterface.LOGOUT) {
      activityMonitor.logout();
    } else if (messageType === ActivityMonitor.events.SHOULD_REFRESH) {
      activityMonitor.beginRefresh();
    }
  }

  private _init(): Promise<boolean> {
    logger.debug(
      `Browsing context: ${this.browsingContextId} joined ${
        this.shortName
      } as ${this.isChild ? 'child' : 'parent'}`
    );

    if (!this.isChild) {
      this.addEventListener('message', (message: any) => {
        // BroadcastChannel is same-origin by design, but validate message
        // structure as defense-in-depth.
        if (!message?.data?.type) {
          return;
        }

        const isCreds = authenticatedEvents.has(message.data.type);
        if (isCreds) {
          logger.log(
            'AuthenticationChannel ${this._browsingContextId} (${this.shortName}) receiving creds',
            message.data
          );
          activityMonitor.emit(
            ActivityMonitor.events.BEGIN_REFRESH,
            message.data
          );
        } else if (
          message.data.type === AuthenticationChannelEvents.BOOTSTRAP_AUTH
        ) {
          // v1 only!
          const version = message.data?.version ?? null;
          if (version === null) {
            this.postMessage({
              type: AuthenticationChannelEvents.BOOTSTRAP_AUTH_RESPONSE,
              payload: this.authenticationContext.getAuthorization(),
              shortName: this.shortName
            });
          } else {
            const transferStateData = {
              ui: JSON.parse(JSON.stringify(this.store?.getState()?.ui ?? {})),
              organization: JSON.parse(
                JSON.stringify(this.store?.getState()?.organization ?? {})
              )
            };

            this.postMessage({
              type: AuthenticationChannelEvents.BOOTSTRAP_AUTH_RESPONSE,
              payload: {
                ...transferStateData
              },
              version: 2,
              shortName: this.shortName
            });
          }
        }
      });
    }

    const promise: Promise<boolean> = new Promise((resolve, reject) => {
      if (!this.isChild) {
        resolve(true);
      } else {
        const authListener = (message: any) => {
          logger.log(
            `AuthenticationChannel ${this.browsingContextId} (${this.shortName}) authListener: ${message.data}`
          );

          const messageType = message.data?.type ?? '';

          if (authenticatedEvents.has(messageType)) {
            const version = message.data?.version ?? null;
            if (version === null) {
              logger.log(
                `AuthenticationChannel ${this.browsingContextId} (${this.shortName} Creds:`,
                message.data
              );
              this.authenticationContext.setAuthorization(message.data.payload);
              activityMonitor.expiryTimeout =
                message.data.payload?.expiryTimeToLive;
              resolve(true);
            } else {
              // run sync
              const uiState = message?.data?.payload?.ui;
              if (!!uiState) {
                const transferData = JSON.parse(JSON.stringify(uiState));
                this.store?.dispatch({
                  type: 'REPLACE_UI_STATE',
                  payload: {
                    ...transferData
                  }
                });
                this.store?.dispatch({
                  type: 'SAVE_SERVICE_LINE_CAPABILITY',
                  payload: transferData?.context?.activeContext?.serviceLineName
                });
              }

              const organizationState = message?.data?.payload?.organization;
              if (!!organizationState) {
                const transferData = JSON.parse(
                  JSON.stringify(organizationState)
                );
                this.store?.dispatch({
                  type: 'REPLACE_ORGANIZATION_STATE',
                  payload: {
                    ...transferData
                  }
                });
              }

              // recreate an authorization object from the UI context.
              const authorization = {
                token: uiState?.context?.jwtToken,
                contextId: uiState?.context?.activeContext?.organizationId
              };

              this.authenticationContext.setAuthorization(authorization);
              // as soon as the channel has received it's credentials, it should
              // establish it's own credentials.
              activityMonitor.emit(ActivityMonitor.events.BEGIN_REFRESH);
              resolve(true);
            }
          }
        };
        this.addEventListener('message', authListener);
        this.postMessage({
          type: AuthenticationChannelEvents.BOOTSTRAP_AUTH,
          version: 2,
          shortName: this.shortName
        });
      }
    });
    return promise;
  }
}
